import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CircularProgress from '@mui/material/CircularProgress'
import ViewContact from '../ViewContact'
// LAP-XXX — Social Dashboard v2 : couleurs canoniques + étoiles éditables inline.
import { RELATIONSHIP_COLORS, StarsEditor } from '../_shared/RelationshipFields'

const LIMIT = 30

const C = {
  bg: '#0B1020',
  border: 'rgba(255,255,255,0.08)',
  borderSoft: 'rgba(255,255,255,0.055)',
  text: '#EAEDF5',
  textSec: '#9AA3BD',
  textMut: '#69728C',
  rowText: '#C4CAD9',
  accent: '#3F7EE8',
}

const AV = [
  ['rgba(79,142,247,0.18)', '#8FB6F7'],
  ['rgba(124,108,240,0.18)', '#B3A8F7'],
  ['rgba(45,189,156,0.16)', '#5FD3B6'],
  ['rgba(224,122,76,0.16)', '#EC9E7E'],
  ['rgba(212,83,126,0.16)', '#E68FAC'],
  ['rgba(224,163,60,0.16)', '#EBC078'],
]

// LAP-XXX — Social Dashboard v2 : +1 colonne "Importance" (rating 0..5 ⭐)
//          et une colonne dédiée pour le pill `relationship_type`.
const COLS =
  '38px minmax(0,2.4fr) minmax(0,1.3fr) minmax(0,1.2fr) minmax(0,0.9fr) minmax(0,0.9fr) minmax(0,1fr)'

const clean = (v) => (v && v !== 'undefined' && v !== 'null' ? String(v) : '')

function avIndex(s) {
  s = s || ''
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return Math.abs(h) % AV.length
}

function nameOf(row) {
  const n = (clean(row.Prenom) + ' ' + clean(row.Nom)).trim()
  return n || clean(row.full_name) || clean(row.surnom) || 'Sans nom'
}

function initialsOf(row) {
  const p = clean(row.Prenom)
  const n = clean(row.Nom)
  if (p || n) return ((p[0] || '') + (n[0] || '')).toUpperCase()
  const f = (clean(row.full_name) || clean(row.surnom)).trim()
  if (f) {
    const parts = f.split(/\s+/)
    return ((parts[0][0] || '') + (parts[1] ? parts[1][0] : '')).toUpperCase()
  }
  return '?'
}

function CheckBox({ checked, onClick }) {
  return (
    <span
      onClick={onClick}
      style={{
        width: 16,
        height: 16,
        borderRadius: 4,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        border: checked ? `1.5px solid ${C.accent}` : '1.5px solid rgba(255,255,255,0.18)',
        background: checked ? C.accent : 'transparent',
      }}
    >
      {checked && (
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      )}
    </span>
  )
}

function CatChip({ label }) {
  if (!label) return <span style={{ color: C.textMut, fontSize: 13 }}>—</span>
  const [bg, fg] = AV[avIndex(label)]
  return (
    <span
      style={{
        fontSize: 11,
        color: fg,
        background: bg,
        padding: '3px 9px',
        borderRadius: 20,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  )
}

// LAP-XXX — Social Dashboard v2 : pill "relationship_type" (family/pro/friendship/intimacy).
function RelationPill({ type, label }) {
  if (!type) return <span style={{ color: C.textMut, fontSize: 13 }}>—</span>
  const color = RELATIONSHIP_COLORS[type] || '#94a3b8'
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: '#0e1320',
        background: color,
        padding: '3px 9px',
        borderRadius: 20,
        whiteSpace: 'nowrap',
        textTransform: 'capitalize',
      }}
    >
      {label || type}
    </span>
  )
}

// LAP-XXX — Social Dashboard v2 : rating compact 0..5 ⭐ pour la liste,
// éditable inline (sauvegarde au clic, sans ouvrir la fiche).
function RatingMini({ contactId, value, onSaved }) {
  return (
    <StarsEditor
      contactId={contactId}
      value={value}
      size={16}
      onSaved={onSaved}
    />
  )
}

const ellipsis = { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

function ListOperations({
  fetchOperations,
  operations,
  setOperations,
  setSelectedRows,
  loading,
}) {
  const { t } = useTranslation('layout')
  const [searchParams, setSearchParams] = useSearchParams()

  const results = operations.results || []
  const count = operations.count || 0
  const hasMore = results.length > 0 && results.length < count

  const [selected, setSelected] = React.useState([])
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [openId, setOpenId] = React.useState('')

  const loadingRef = React.useRef(false)
  const sentinelRef = React.useRef(null)
  const scrollRef = React.useRef(null)
  const fetchRef = React.useRef(fetchOperations)
  fetchRef.current = fetchOperations

  const filterSig = React.useMemo(() => {
    const p = new URLSearchParams(searchParams)
    p.delete('cid')
    return p.toString()
  }, [searchParams])

  React.useEffect(() => {
    const cid = searchParams.get('cid')
    if (cid && parseInt(cid, 10)) setOpenId(String(cid))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    setSelected([])
    setSelectedRows([])
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0 })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSig])

  React.useEffect(() => {
    const node = sentinelRef.current
    if (!node) return undefined
    const observer = new IntersectionObserver(
      async (entries) => {
        if (!entries[0].isIntersecting || loadingRef.current || !hasMore) return
        loadingRef.current = true
        setLoadingMore(true)
        const pag = `${filterSig ? filterSig + '&' : ''}limit=${LIMIT}&offset=${results.length}`
        await fetchRef.current(pag, true)
        loadingRef.current = false
        setLoadingMore(false)
      },
      { root: scrollRef.current, rootMargin: '240px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [hasMore, results.length, filterSig])

  const openContact = (id) => {
    setOpenId(String(id))
    const next = new URLSearchParams(searchParams)
    next.set('cid', String(id))
    setSearchParams(next)
  }

  const closeContact = () => {
    setOpenId('')
    const next = new URLSearchParams(searchParams)
    next.delete('cid')
    setSearchParams(next)
  }

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
      setSelectedRows(next)
      return next
    })
  }

  // LAP-XXX — Patch d'un contact dans la liste (rating, relationship_type, …)
  // depuis n'importe où (StarsEditor de la row OU ViewContact).
  // Met à jour `operations.results` de façon IMMUTABLE pour re-render React.
  const patchContact = React.useCallback(
    (patch) => {
      if (!patch || !patch.id) return
      if (typeof setOperations !== 'function') return
      setOperations((prev) => {
        if (!prev || !Array.isArray(prev.results)) return prev
        const next = prev.results.map((r) =>
          r.id === patch.id ? { ...r, ...patch } : r,
        )
        return { ...prev, results: next }
      })
    },
    [setOperations],
  )

  const pageIds = results.map((r) => r.id)
  const allSelected = results.length > 0 && pageIds.every((id) => selected.includes(id))
  const toggleAll = () => {
    const next = allSelected ? [] : pageIds
    setSelected(next)
    setSelectedRows(next)
  }

  const hdr = { fontSize: 11, color: C.textMut }

  return (
    <>
      <style>{`.lc-row{transition:background .12s}.lc-row:hover{background:rgba(255,255,255,0.045)!important}`}</style>
      <div
        style={{
          background: C.bg,
          border: `1px solid ${C.border}`,
          borderRadius: 14,
          height: '75vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          marginBottom: 40,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '15px 20px',
            borderBottom: `1px solid ${C.border}`,
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 500, color: C.text }}>
            {t('contacts.pageTitle')}
          </span>
          <span
            style={{
              fontSize: 12,
              color: C.textSec,
              background: 'rgba(255,255,255,0.07)',
              padding: '3px 10px',
              borderRadius: 20,
            }}
          >
            {count}
          </span>
        </div>

        <div ref={scrollRef} style={{ overflowY: 'auto', flex: 1 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: COLS,
              gap: 12,
              alignItems: 'center',
              padding: '11px 20px',
              position: 'sticky',
              top: 0,
              background: C.bg,
              zIndex: 2,
              borderBottom: `1px solid ${C.border}`,
            }}
          >
            <CheckBox checked={allSelected} onClick={toggleAll} />
            <span style={hdr}>Contact</span>
            <span style={hdr}>{t('contacts.headerPortable')}</span>
            <span style={hdr}>{t('contacts.headerSociete')}</span>
            <span style={hdr}>{t('contacts.headerVille')}</span>
            {/* LAP-XXX — Social Dashboard v2 */}
            <span style={hdr}>{t('social.contact.rating.label', 'Importance')}</span>
            <span style={hdr}>
              {t('social.contact.relationshipType.label', 'Type de relation')}
            </span>
          </div>

          {results.length === 0 && loading && (
            <div style={{ padding: '64px 20px', display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={26} sx={{ color: C.accent }} />
            </div>
          )}

          {results.length === 0 && !loading && (
            <div
              style={{ padding: '64px 20px', textAlign: 'center', color: C.textMut, fontSize: 14 }}
            >
              {t('contacts.noDataFound')}
            </div>
          )}

          {results.map((row) => {
            const [abg, afg] = AV[avIndex(nameOf(row))]
            const sub = clean(row.Job) || clean(row.Societe) || 'Aucune société'
            const cat = row.Category && row.Category[0] ? clean(row.Category[0].Categorie) : ''
            const isSel = selected.includes(row.id)
            // LAP-XXX — Social Dashboard v2 : rating + relationship_type.
            const ratingValue = Number(row.rating || 0)
            const relType = row.relationship_type || null
            const relLabel =
              row.relationship_type_label ||
              (relType
                ? t(`social.contact.relationshipType.${relType}`, relType)
                : null)
            // LAP-XXX — patch immutable de la row : déclenche un re-render
            // React (les mutations en place ne sont pas détectées).
            const onRatingSaved = (next) =>
              patchContact({
                id: row.id,
                rating: next.rating,
                relationship_type: next.relationship_type,
                relationship_type_label: next.relationship_type_label,
              })
            return (
              <div
                key={row.id}
                className="lc-row"
                onClick={() => openContact(row.id)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: COLS,
                  gap: 12,
                  alignItems: 'center',
                  padding: '10px 20px',
                  borderTop: `1px solid ${C.borderSoft}`,
                  cursor: 'pointer',
                  background: isSel ? 'rgba(63,126,232,0.08)' : 'transparent',
                }}
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleRow(row.id)
                  }}
                  style={{ display: 'flex' }}
                >
                  <CheckBox checked={isSel} />
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 11, minWidth: 0 }}>
                  <span
                    style={{
                      width: 38,
                      height: 38,
                      borderRadius: '50%',
                      background: abg,
                      color: afg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 13,
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    {initialsOf(row)}
                  </span>
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{ display: 'block', fontSize: 14, color: C.text, fontWeight: 500, ...ellipsis }}
                    >
                      {nameOf(row)}
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: C.textMut, ...ellipsis }}>
                      {sub}
                    </span>
                  </span>
                </span>
                <span style={{ fontSize: 13, color: clean(row.Portable) ? C.rowText : C.textMut, ...ellipsis }}>
                  {clean(row.Portable) || '—'}
                </span>
                <span style={{ fontSize: 13, color: clean(row.Societe) ? C.rowText : C.textMut, ...ellipsis }}>
                  {clean(row.Societe) || '—'}
                </span>
                <span style={{ fontSize: 13, color: clean(row.Ville) ? C.rowText : C.textMut, ...ellipsis }}>
                  {clean(row.Ville) || '—'}
                </span>
                {/* LAP-XXX — Social Dashboard v2 : étoiles éditables inline */}
                <span onClick={(e) => e.stopPropagation()}>
                  <RatingMini
                    contactId={row.id}
                    value={ratingValue}
                    onSaved={onRatingSaved}
                  />
                </span>
                <span><RelationPill type={relType} label={relLabel} /></span>
              </div>
            )
          })}

          <div ref={sentinelRef} style={{ height: 1 }} />

          {loadingMore && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 10,
                padding: 18,
                borderTop: `1px solid ${C.borderSoft}`,
              }}
            >
              <CircularProgress size={15} sx={{ color: C.accent }} />
              <span style={{ fontSize: 12, color: C.textMut }}>
                Chargement des contacts suivants…
              </span>
            </div>
          )}

          {!hasMore && results.length > 0 && (
            <div
              style={{
                textAlign: 'center',
                padding: 18,
                fontSize: 12,
                color: C.textMut,
                borderTop: `1px solid ${C.borderSoft}`,
              }}
            >
              {count} contact{count > 1 ? 's' : ''} · fin de liste
            </div>
          )}
        </div>
      </div>

      {openId && (
        <ViewContact
          id={openId}
          setModalOpen={closeContact}
          onContactPatched={patchContact}
        />
      )}
    </>
  )
}

export default ListOperations
